<template>
  <modal id="dataDownForm"
         v-model="visible"
         :title="$t('ed-dataDownForm:sendDataDown')"
         :disable-validate-button="disableModalButtons"
         :callback="sendDataDown"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('ed-dataDownForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="fPort" class="control-label">{{ $t('ed-dataDownForm:fPort') | capitalize }} *</label>
          <input id="fPort"
                 v-model="fPort"
                 v-validate="'required|number|between:0,255'"
                 :data-vv-as="$t('ed-dataDownForm:fPort')"
                 :class="{ 'is-invalid': errors.has('fPort') }"
                 type="text"
                 name="fPort"
                 class="form-control"
                 min="0"
                 max="255"
          >
          <div class="invalid-feedback">{{ errors.first('fPort') }}</div>
        </div>
      </div>

      <div class="form-group">
        <label for="contentType" class="control-label d-block">{{ $t('ed-dataDownForm:payloadType') | capitalize }} *</label>
        <input id="hexa"
               v-model="contentType"
               v-validate="'required'"
               :data-vv-as="$t('ed-dataDownForm:payloadType')"
               :class="{ 'is-invalid': errors.has('contentType') }"
               type="radio"
               value="HEXA"
               name="contentType"
        >
        <label for="hexa" class="control-label">{{ $t('ed-dataDownForm:hexadecimal') | capitalize }}</label>

        <input id="text"
               v-model="contentType"
               v-validate="'required'"
               :class="{ 'is-invalid': errors.has('contentType') }"
               type="radio"
               value="TEXT"
               name="contentType"
        >
        <label for="text" class="control-label">{{ $t('ed-dataDownForm:text') | capitalize }}</label>

        <div class="invalid-feedback">{{ errors.first('contentType') }}</div>
      </div>

      <div class="form-group">
        <label for="payload" class="control-label">{{ $t('ed-dataDownForm:payload') | capitalize }} *</label>
        <textarea id="payload"
                  v-model="payload"
                  v-validate="payloadValidator"
                  :data-vv-as="$t('ed-dataDownForm:payload')"
                  :class="{ 'is-invalid': errors.has('payload') }"
                  class="form-control"
                  name="payload"
                  rows="5"
        />
        <div class="invalid-feedback">{{ errors.first('payload') }}</div>
      </div>

      <div class="form-group">
        <label class="control-label d-block">{{ $t('ed-dataDownForm:confirmed') | capitalize }}</label>
        <input id="confirmed" v-model="confirmed" type="checkbox" name="confirmed">
        <label for="confirmed" />
        <label for="confirmed" class="control-label">{{ $t('ed-dataDownForm:confirmedEnable') | capitalize }}</label>
      </div>

      <div v-if="confirmed" class="form-row">
        <div class="form-group col-sm-6">
          <label for="maxAttempts" class="control-label">{{ $t('ed-dataDownForm:maxAttempts') | capitalize }} *</label>
          <input id="maxAttempts"
                 v-model="maxAttempts"
                 v-validate="{rules: { required:confirmed, number:true, min_value:0 }}"
                 :data-vv-as="$t('ed-dataDownForm:maxAttempts')"
                 :class="{ 'is-invalid': errors.has('maxAttempts') }"
                 type="text"
                 name="maxAttempts"
                 class="form-control"
                 min="0"
          >
          <div class="invalid-feedback">{{ errors.first('maxAttempts') }}</div>
        </div>

        <div class="form-group col-sm-6">
          <label for="ttl" class="control-label">{{ $t('ed-dataDownForm:ttl') | capitalize }} *</label>
          <input id="ttl"
                 v-model="ttl"
                 v-validate="{rules: { required:confirmed, number:true, min_value:0 }}"
                 :data-vv-as="$t('ed-dataDownForm:ttl')"
                 :class="{ 'is-invalid': errors.has('ttl') }"
                 type="text"
                 name="ttl"
                 class="form-control"
                 min="0"
          >
          <small class="form-text text-muted">{{ $t('ed-dataDownForm:ttlUnit') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('ttl') }}</div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { pick } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      disableModalButtons: false,
      // Form
      fPort: '',
      contentType: 'HEXA',
      payload: '',
      confirmed: false,
      maxAttempts: '',
      ttl: ''
    }
  },

  computed: {
    payloadValidator () {
      return {
        rules: {
          required: true,
          payloadHex: this.contentType === 'HEXA',
          // Limit payload length (51 bytes)
          // The hexa size must be set to 102 char.
          // The text length must be set to 51 char.
          max: this.contentType === 'HEXA' ? 102 : 51
        }
      }
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show
    },
    visible () {
      this.$emit('change', this.visible)
    }
  },

  methods: {
    async reset () {
      this.fPort = ''
      this.contentType = 'HEXA'
      this.payload = ''
      this.confirmed = false
      this.maxAttempts = ''
      this.ttl = ''

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async sendDataDown () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      // Disable modal buttons to prevent multiple requests
      this.disableModalButtons = true

      const params = pick(this, ['fPort', 'contentType', 'payload', 'confirmed', 'maxAttempts', 'ttl'])
      params.endDevice = { devEui: this.$store.getters.endDevice.devEui }
      let error
      [error] = await to(this.$api.createEndDeviceDataDown(this.$store.getters.application, params))
      if (error) return

      this.visible = false
      this.disableModalButtons = false
      // Success
      eventBus.$emit('message-success', this.$t('ed-dataDownForm:sendSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    }
  }
}
</script>
